import { isDirection, type Direction } from './direction';
import { CRITTER_KINDS, type CritterSpawn } from './critters';
import type { BallSpec } from './balls';
import { Grid, Tile, isSolid, isTile, type ExtraCell } from './grid';

/**
 * `treasure` (1 coin) and `hoard` (25): a secret yard's bonus coins, paid like coins on a clear but
 * never counted toward the all-coins star.
 */
export type PickupKind =
  | 'coin'
  | 'gas'
  | 'repair'
  | 'rare'
  | 'treasure'
  | 'hoard'
  /** Item boxes (src/sim/items.ts): the item is held, to fire on demand. */
  | 'turbo'
  | 'trim';
export const PICKUP_KINDS: readonly PickupKind[] = [
  'coin',
  'gas',
  'repair',
  'rare',
  'treasure',
  'hoard',
  'turbo',
  'trim',
];

/** What a treasure pickup is worth in coins. */
export const TREASURE_VALUE: Partial<Record<PickupKind, number>> = { treasure: 1, hoard: 25 };

export interface PickupSpawn {
  kind: PickupKind;
  x: number;
  y: number;
}

/** Everything the sim needs to start a level. Produced by content loading or the generator. */
export interface SimLevel {
  id: string;
  /** Seed for any in-level randomness (critters, spawners). */
  seed: string;
  width: number;
  height: number;
  /** Row-major, length `width * height`. */
  tiles: readonly Tile[];
  /**
   * Break tier of the lawn item on each tile (row-major; -1 or absent: nothing breakable). A
   * mower smashes items up to its break power and crashes into tougher ones.
   */
  toughness?: readonly number[];
  /** Cells beyond the lawn: a secret yard and the gate into it (sim/grid ExtraCell). */
  extras?: readonly ExtraCell[];
  /**
   * Regrowing meadow (Rolling Meadows): patches of wildflower meadow grass. A patch left half
   * mowed for `delayTiles` of the mower's own travel grows back; a finished patch stays mowed.
   */
  regrow?: RegrowSpec;
  start: { x: number; y: number; dir: Direction };
  /**
   * Head to head (docs/HEAD_TO_HEAD.md): where each player's mower starts, seat 0 first (it is
   * `start`). Two or more seats make a match; without them the lawn has its one mower.
   */
  seats?: readonly { x: number; y: number; dir: Direction }[];
  /** Collectibles, picked up by driving onto their tile. */
  pickups?: readonly PickupSpawn[];
  /** Critters and where they live. */
  critters?: readonly CritterSpawn[];
  /**
   * Boss: Beat the Storm. A rain front rolls up from the bottom edge; reaching the top row wins,
   * being caught ends the run. Timing is in the mower's own tiles (its ticks per tile), so the
   * storm chases a fast mower as hard as a slow one.
   */
  storm?: StormSpec;
  /**
   * Boss: Lawn Art. Each tile should end up striped a given way (across = mowed east/west, along =
   * mowed north/south; the last pass counts). Mowing everything does not end the lawn: driving
   * onto the judges' table does, once the whole lawn is mowed.
   */
  art?: ArtSpec;
  /**
   * Boss: Round Up the Sheep. Nudge every sheep into the pen (drive into one to push it a tile
   * on) and mow the whole lawn.
   */
  herd?: HerdSpec;
  /**
   * A sheep round-up match (docs/ROUNDUP_MATCH.md): every seat has its own pen at its own end, and
   * a sheep nudged through a seat's gate counts for that seat. Needs seats.
   */
  roundup?: RoundupSpec;
  /**
   * A hedge maze match (docs/MAZE_MATCH.md): the first seat onto the plaza in the middle wins; at
   * the whistle, the one fewer tiles from it along the paths. Needs seats.
   */
  maze?: MazeSpec;
  /**
   * Boss: Rival Robo-Mower. The neighbor's robot mows the same lawn; whoever mows more of it
   * wins.
   */
  rival?: RivalSpec;
  /**
   * Moles: now and then one pushes up a molehill through lawn already mowed, which must be
   * flattened (mowed again) before the lawn is done.
   */
  moles?: MoleSpec;
  /**
   * Falling trees: each dead tree creaks for a moment, then comes down the way it leans, leaving
   * a log across the lawn (an obstacle) and a stump where it stood.
   */
  falls?: readonly FallSpec[];
  /**
   * Boss: Lights Out. Night has fallen: only the mower's headlamp and the old lanterns it lights
   * (by passing beside them) show the lawn. A normal lawn for the sim; the dark is drawn.
   */
  dark?: boolean;
  /**
   * Hills (the Grand Estate): grassy mounds. Driving up toward the top takes longer and burns more
   * gas; driving down away from it is quicker; going round it, and across the flat top, is level.
   */
  hills?: readonly HillSpec[];
  /**
   * Hedge Maze: the garden party starts after this many tiles' worth of driving (paced to the
   * mower): finish before then.
   */
  party?: { tiles: number };
  /**
   * Stray balls (the Stadium): now and then a whistle, and a ball comes rolling straight across
   * the lawn from one edge until it meets something. A hit costs a heart.
   */
  balls?: BallSpec;
  /**
   * Boss: mower soccer (the Stadium's finale, owner 2026-10-05). Drive into the ball to kick it;
   * a ball in either goal mouth scores and goes back to the center spot. The whistle ends the
   * half: enough goals by then wins. Mowing the pitch is optional (coins and a star).
   */
  soccer?: SoccerSpec;
}

export interface SoccerSpec {
  /** The goal mouths: a ball rolling onto one of these tiles scores. */
  mouths: readonly { x: number; y: number }[];
  /** The center spot, where the ball starts and returns after a goal. */
  center: { x: number; y: number };
  /** Goals needed by halftime. */
  target: number;
  /** The half's length, in tiles' worth of the mower's driving (fair to slow and fast mowers). */
  halfTiles: number;
  /** The second and third stars: goals scored, and the share of the pitch mowed. */
  stars: { goals: number; mowed: number };
  /**
   * A soccer match (docs/SOCCER_MATCH.md): the whistle, in ticks from the kick-off. In a match
   * `target` is the goals that win at once, and each seat attacks the goal at the far end.
   */
  matchTicks?: number;
}

/** A mound filling a rectangle of lawn (its top in the middle). */
export interface HillSpec {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Which way the ground falls at (x, y) on a mound: away from its middle, along whichever axis
 * the tile is further out on (columns win a tie); null on the flat top or off the mound.
 */
export function moundSlope(hill: HillSpec, x: number, y: number): Direction | null {
  if (x < hill.x || y < hill.y || x >= hill.x + hill.w || y >= hill.y + hill.h) return null;
  const ux = (x - (hill.x + (hill.w - 1) / 2)) / (hill.w / 2);
  const uy = (y - (hill.y + (hill.h - 1) / 2)) / (hill.h / 2);
  if (Math.abs(ux) < 0.34 && Math.abs(uy) < 0.34) return null;
  if (Math.abs(ux) >= Math.abs(uy)) return ux > 0 ? 1 : 3;
  return uy > 0 ? 2 : 0;
}

export interface RegrowSpec {
  /** Each patch's tiles (row-major indices). */
  patches: readonly (readonly number[])[];
  /** How long a half-mowed patch waits before growing back, in tiles of the mower's travel. */
  delayTiles: number;
}

export interface FallSpec {
  /** The dead tree (an obstacle tile). */
  x: number;
  y: number;
  /** Which way it falls. */
  dir: Direction;
  /** Tiles' worth of driving before it starts to creak (paced to the mower). */
  at: number;
  /** Length of the log it leaves, in tiles beyond the stump. */
  length: number;
}

/** Tiles a fallen tree's log covers. */
export function fallTiles(f: FallSpec): { x: number; y: number }[] {
  const ddx = f.dir === 1 ? 1 : f.dir === 3 ? -1 : 0;
  const ddy = f.dir === 2 ? 1 : f.dir === 0 ? -1 : 0;
  return Array.from({ length: f.length }, (_, i) => ({
    x: f.x + ddx * (i + 1),
    y: f.y + ddy * (i + 1),
  }));
}

export interface MoleSpec {
  /** Molehills in all. */
  hills: number;
  /** Tiles' worth of driving before the first one (paced to the mower). */
  first: number;
  /** Tiles' worth of driving between one and the next. */
  every: number;
}

export interface RivalSpec {
  /** The robot's charging dock, where it sets off from. */
  start: { x: number; y: number; dir: Direction };
  /** Its speed as a percentage of the player's mower (paced to the mower, like the storm). */
  speed: number;
  /** Share of the grass (0..1) the player must mow for the boss's second star. */
  target: number;
}

export interface HerdSpec {
  /** The fenced pen's tiles, where penned sheep stand (the mower can't go in). */
  pen: readonly { x: number; y: number }[];
  /** The open gate in front of the pen: a sheep nudged onto it trots in and is penned for good. */
  gate: { x: number; y: number };
}

export interface RoundupSpec {
  /** Each seat's pen and gate, seat 0's first. */
  pens: readonly HerdSpec[];
  /** Sheep penned that win at once (more than half of them). */
  target: number;
  /** The whistle, in ticks from the start: then the most sheep penned wins. */
  matchTicks: number;
}

export interface MazeSpec {
  /** The plaza in the middle: driving onto any of these tiles wins. */
  plaza: readonly { x: number; y: number }[];
  /** The whistle, in ticks from the start: then the one closer to the plaza wins. */
  matchTicks: number;
}

export interface StormSpec {
  /** Tiles' worth of driving before the rain starts moving. */
  delayTiles: number;
  /** Tiles' worth of driving per row the rain advances (above 1: slower than the mower). */
  pace: number;
  /** Share of the grass (0..1) to mow for the boss's second star. */
  target: number;
  /** Rain Chase: the pace drops by this much per row the rain climbs, down to `minPace`. */
  accel?: number;
  minPace?: number;
}

/** Which way a tile should be striped in a lawn-art picture. */
export const ArtAxis = { None: 0, Across: 1, Along: 2 } as const;
export type ArtAxis = (typeof ArtAxis)[keyof typeof ArtAxis];

export interface ArtSpec {
  /** Row-major, length width * height: the stripe each tile should end up with (None = no target). */
  pattern: readonly ArtAxis[];
  /**
   * The judges' table: driving onto it with the whole lawn mowed finishes the lawn. Absent (the
   * picture-only canvas, owner 2026-10-05): the lawn finishes as the last of the picture is mowed.
   */
  goal?: { x: number; y: number };
  /** Picture match (0..1) for the second and third stars. */
  stars: readonly [number, number];
}

/** Throws with a descriptive message if the level cannot be simulated. */
export function validateSimLevel(level: SimLevel): void {
  const { width, height, tiles, start } = level;
  if (tiles.length !== width * height) {
    throw new RangeError(`${level.id}: expected ${width * height} tiles, got ${tiles.length}`);
  }
  if (!tiles.every(isTile)) throw new RangeError(`${level.id}: unknown tile value`);
  if (tiles.includes(Tile.Mowed)) throw new RangeError(`${level.id}: starts with mowed tiles`);
  if (!isDirection(start.dir)) throw new RangeError(`${level.id}: invalid start direction`);
  const grid = new Grid(width, height, tiles, level.toughness, level.extras);
  const startTile = grid.tileAt(start.x, start.y);
  if (!grid.inBounds(start.x, start.y) || isSolid(startTile) || startTile === Tile.Brush) {
    throw new RangeError(`${level.id}: start (${start.x},${start.y}) is blocked or out of bounds`);
  }
  if (level.seats) {
    const [first] = level.seats;
    if (level.seats.length < 2 || !first || first.x !== start.x || first.y !== start.y) {
      throw new RangeError(`${level.id}: seats need two or more, the first at the start`);
    }
    const taken = new Set<number>();
    for (const s of level.seats) {
      const t = grid.tileAt(s.x, s.y);
      if (!grid.inBounds(s.x, s.y) || isSolid(t) || t === Tile.Brush || !isDirection(s.dir)) {
        throw new RangeError(`${level.id}: seat (${s.x},${s.y}) is blocked or out of bounds`);
      }
      if (taken.has(s.y * width + s.x)) throw new RangeError(`${level.id}: two seats share a tile`);
      taken.add(s.y * width + s.x);
    }
  }
  for (const p of level.pickups ?? []) {
    if (!PICKUP_KINDS.includes(p.kind))
      throw new RangeError(`${level.id}: unknown pickup ${p.kind}`);
    if (
      !grid.inPlay(p.x, p.y) ||
      grid.isBlocked(p.x, p.y) ||
      grid.tileAt(p.x, p.y) === Tile.Brush
    ) {
      throw new RangeError(`${level.id}: ${p.kind} at (${p.x},${p.y}) is unreachable`);
    }
  }
  const seen = new Set<string>();
  for (const c of level.critters ?? []) {
    if (!CRITTER_KINDS.includes(c.kind))
      throw new RangeError(`${level.id}: unknown critter ${c.kind}`);
    const t = grid.tileAt(c.x, c.y);
    if (!grid.inBounds(c.x, c.y) || isSolid(t) || t === Tile.Brush) {
      throw new RangeError(`${level.id}: ${c.kind} at (${c.x},${c.y}) can't stand there`);
    }
    if (Math.abs(c.x - start.x) + Math.abs(c.y - start.y) <= 1) {
      throw new RangeError(`${level.id}: ${c.kind} at (${c.x},${c.y}) is on top of the start`);
    }
    const key = `${c.x},${c.y}`;
    if (seen.has(key)) throw new RangeError(`${level.id}: two critters at (${key})`);
    seen.add(key);
  }
  if (level.moles && (level.moles.hills < 0 || level.moles.first < 1 || level.moles.every < 1)) {
    throw new RangeError(`${level.id}: invalid moles`);
  }
  if (level.balls && (level.balls.first < 1 || level.balls.every < 1)) {
    throw new RangeError(`${level.id}: invalid balls`);
  }
  if (level.party && (!Number.isInteger(level.party.tiles) || level.party.tiles < 1)) {
    throw new RangeError(`${level.id}: invalid party time`);
  }
  for (const hill of level.hills ?? []) {
    if (
      hill.w < 1 ||
      hill.h < 1 ||
      hill.x < 0 ||
      hill.y < 0 ||
      hill.x + hill.w > level.width ||
      hill.y + hill.h > level.height
    ) {
      throw new RangeError(`${level.id}: invalid hill at (${hill.x},${hill.y})`);
    }
  }
  for (const f of level.falls ?? []) {
    const inside = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < level.width && y < level.height;
    if (!isDirection(f.dir) || f.at < 1 || f.length < 1 || !inside(f.x, f.y)) {
      throw new RangeError(`${level.id}: invalid falling tree at (${f.x},${f.y})`);
    }
    if (level.tiles[f.y * level.width + f.x] !== Tile.Obstacle) {
      throw new RangeError(`${level.id}: falling tree at (${f.x},${f.y}) is not an obstacle tile`);
    }
    if (fallTiles(f).some((p) => !inside(p.x, p.y))) {
      throw new RangeError(`${level.id}: tree at (${f.x},${f.y}) falls off the lawn`);
    }
  }
  if (level.rival) {
    const { x, y } = level.rival.start;
    if (!grid.inBounds(x, y) || grid.isBlocked(x, y) || (x === start.x && y === start.y)) {
      throw new RangeError(`${level.id}: the robot's dock at (${x},${y}) is blocked`);
    }
    if (level.rival.speed <= 0) throw new RangeError(`${level.id}: the robot needs a speed`);
  }
  if (level.herd) {
    const { gate, pen } = level.herd;
    if (!grid.inBounds(gate.x, gate.y) || grid.isBlocked(gate.x, gate.y)) {
      throw new RangeError(`${level.id}: the pen gate at (${gate.x},${gate.y}) is blocked`);
    }
    const sheep = (level.critters ?? []).filter((c) => c.kind === 'sheep').length;
    if (pen.length < sheep) throw new RangeError(`${level.id}: the pen is too small for the sheep`);
  }
  if (level.roundup) {
    const { pens, target, matchTicks } = level.roundup;
    if (pens.length !== (level.seats?.length ?? 0)) {
      throw new RangeError(`${level.id}: a round-up needs a pen for every seat`);
    }
    for (const { gate, pen } of pens) {
      if (!grid.inBounds(gate.x, gate.y) || grid.isBlocked(gate.x, gate.y)) {
        throw new RangeError(`${level.id}: the pen gate at (${gate.x},${gate.y}) is blocked`);
      }
      if (pen.length < target) throw new RangeError(`${level.id}: a pen is too small`);
    }
    if (target <= 0 || matchTicks <= 0)
      throw new RangeError(`${level.id}: a round-up needs a goal`);
  }
  if (level.maze) {
    const { plaza, matchTicks } = level.maze;
    if ((level.seats?.length ?? 0) < 2) throw new RangeError(`${level.id}: a maze needs seats`);
    if (plaza.length === 0 || matchTicks <= 0) {
      throw new RangeError(`${level.id}: a maze needs a plaza and a whistle`);
    }
    for (const p of plaza) {
      if (!grid.inBounds(p.x, p.y) || grid.isBlocked(p.x, p.y)) {
        throw new RangeError(`${level.id}: the plaza at (${p.x},${p.y}) is blocked`);
      }
    }
  }
}
